/**
 * S2, second half — one area's sessions: resume mine, view someone else's, start
 * a new one.
 *
 * Three distinct affordances because the states are genuinely different (phase 3
 * doc § 3): **Resume** (mine, editable), **View** (someone else's — read-only, and
 * no draft is kept on this device), **Start a count**. Which one a session gets
 * is decided in `sessions/rules.ts` and nowhere else, so the button a manager
 * sees and the rule the server enforces cannot disagree.
 *
 * The list is deliberately unfiltered by owner. A manager who can see the area
 * can see its counts — `docs/spec.md:101` says others may *view* — and hiding
 * somebody else's open count is how two people end up counting the same shelf.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAreaSessions, useCountAreas, useStartSession } from "../api/queries";
import type { CountSession } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { formatPostingDate, formatTimestamp } from "../sessions/format";
import {
  affordanceFor,
  isMine,
  sessionPath,
  startWouldBeConcurrent,
  todayInTampa,
} from "../sessions/rules";

export function AreaSessions() {
  const { areaId = "" } = useParams();
  const navigate = useNavigate();
  const { signedIn } = useAuth();
  const [activeOnly, setActiveOnly] = useState(true);
  const [postingDate, setPostingDate] = useState(todayInTampa);
  const [concurrentAcknowledged, setConcurrentAcknowledged] = useState(false);

  // The principal comes from the server (`GET /count/areas`), never from the dev
  // shim's remembered string: "mine" has to mean the identity the backend
  // authenticated, or the UI and the ownership lock are comparing different
  // things. It is also already cached — S2's area list just read it.
  const areas = useCountAreas(signedIn);
  const principal = areas.data?.principal ?? null;

  const { data, isLoading, error, refetch, isFetching } = useAreaSessions(
    areaId,
    activeOnly,
    signedIn && Boolean(areaId),
  );
  const start = useStartSession(areaId);

  if (!signedIn) {
    return (
      <section className="card">
        <h2>Not signed in</h2>
        <p>
          <Link to="/">Sign in</Link> to see this area's counts.
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="card error">
        <h2>Could not load this area's counts</h2>
        <p>{(error as Error).message}</p>
        <p className="muted">
          A 403 means this area is not assigned to you, and a 404 means it is not
          one of the six — both are answers from the server, relayed as it wrote
          them.
        </p>
        <p>
          <Link to="/areas">Back to your areas</Link>
        </p>
      </section>
    );
  }

  const sessions = data?.sessions ?? [];
  const area = data?.area;
  // Filtered to active sessions inside the rules module, so the show-all toggle
  // cannot turn a posted count from last Friday into a concurrency warning.
  const concurrent = startWouldBeConcurrent(sessions);
  const mineActive = concurrent.filter((session) => isMine(session, principal));
  const needsAcknowledgement = concurrent.length > 0;
  const canStart = !needsAcknowledgement || concurrentAcknowledged;

  const startCount = () => {
    start.mutate(postingDate, {
      onSuccess: (session) => {
        // A brand-new session is Open and mine, so this lands on entry. Routing
        // through the same rule as everything else means there is no second
        // opinion about where a session opens.
        const { route } = affordanceFor(session, principal);
        navigate(sessionPath(areaId, session, route));
      },
    });
  };

  return (
    <section>
      <h2>{area?.display_name ?? areaId}</h2>
      <p className="muted">
        {area ? (
          <>
            {area.company_name} · <code>{area.location_code}</code> · {area.area_type}
          </>
        ) : (
          <>Loading this area…</>
        )}
      </p>
      <p>
        <Link to="/areas">← All your areas</Link>
      </p>

      {/* --- Start a count ------------------------------------------------ */}
      <div className="card">
        <h3>Start a count</h3>
        <label htmlFor="posting-date">Posting date</label>
        <input
          id="posting-date"
          name="posting-date"
          type="date"
          value={postingDate}
          onChange={(event) => setPostingDate(event.target.value)}
        />
        <p className="muted small">
          Defaults to today in Tampa. Business Central judges whether the date is
          postable — its posting window, and any closed inventory period — at
          posting time, in Tampa time. Staging accepts the date either way.
        </p>

        {needsAcknowledgement && (
          <div className="notice" data-testid="concurrent-warning">
            <p>
              <strong>
                {concurrent.length === 1
                  ? "This area already has a count in progress."
                  : `This area already has ${concurrent.length} counts in progress.`}
              </strong>{" "}
              {mineActive.length > 0
                ? "One of them is yours — resuming it keeps everything you have already entered."
                : "None of them are yours."}
            </p>
            <p className="muted small">
              Business Central permits more than one open count for an area, and
              this app does not pretend otherwise: nothing in the staging tables or
              the posting codeunit refuses a second one. But each session posts
              its own adjustments separately, so two half-finished counts of the
              same shelves can both reach the ledger.
            </p>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={concurrentAcknowledged}
                onChange={(event) => setConcurrentAcknowledged(event.target.checked)}
              />{" "}
              Start a second count for this area anyway
            </label>
          </div>
        )}

        <button
          type="button"
          onClick={startCount}
          disabled={!canStart || start.isPending || !postingDate}
        >
          {start.isPending ? "Starting…" : "Start a count"}
        </button>
        {start.error && (
          <p className="error-text" data-testid="start-error">
            {(start.error as Error).message}
          </p>
        )}
      </div>

      {/* --- The sessions ------------------------------------------------- */}
      <div className="list-header">
        <h3>{activeOnly ? "Counts in progress" : "Every count staged for this area"}</h3>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={!activeOnly}
            onChange={(event) => setActiveOnly(!event.target.checked)}
          />{" "}
          Include finished and cancelled
        </label>
      </div>

      {isLoading ? (
        <p className="muted">Reading this area's counts from Business Central…</p>
      ) : sessions.length === 0 ? (
        <div className="card">
          <p>
            {activeOnly
              ? "No count is in progress for this area. Starting one is above."
              : "Business Central holds no count sessions for this area at all."}
          </p>
        </div>
      ) : (
        <ul className="session-list">
          {sessions.map((session) => (
            <SessionRow
              key={session.session_id}
              areaId={areaId}
              session={session}
              principal={principal}
            />
          ))}
        </ul>
      )}

      <button type="button" onClick={() => void refetch()} disabled={isFetching}>
        {isFetching ? "Refreshing…" : "Refresh"}
      </button>
    </section>
  );
}

/**
 * One session. Everything on it is a fact BC holds — status, owner, posting
 * date, how many lines are staged — and the button is whatever the rules module
 * says this session's state affords.
 */
function SessionRow({
  areaId,
  session,
  principal,
}: {
  areaId: string;
  session: CountSession;
  principal: string | null;
}) {
  const mine = isMine(session, principal);
  const affordance = affordanceFor(session, principal);

  return (
    <li>
      <div className="session-card" data-testid={`session-${session.session_id}`}>
        <div className="session-card__top">
          <span
            className={`chip chip--${session.status.toLowerCase()}`}
            data-testid={`status-${session.session_id}`}
          >
            {session.status}
          </span>
          <span className={mine ? "owner owner--mine" : "owner"}>
            {mine ? "Yours" : session.responsible_manager || "unassigned"}
          </span>
        </div>

        {/* The failure, before anything else on the card and before the button
            that leads to the screen which offers to clear it. */}
        {session.error_text && (
          <p className="error-text" data-testid={`error-${session.session_id}`}>
            <strong>Business Central refused this post:</strong> {session.error_text}
          </p>
        )}

        <dl className="facts">
          <div>
            <dt>Posting date</dt>
            <dd>{formatPostingDate(session.posting_date)}</dd>
          </div>
          <div>
            <dt>Items counted</dt>
            {/* A real count of staged lines, read from staging — not the length
                of a list this screen deliberately did not load. */}
            <dd data-testid={`lines-${session.session_id}`}>{session.line_count}</dd>
          </div>
          <div>
            <dt>Started</dt>
            <dd>{formatTimestamp(session.created_at)}</dd>
          </div>
          {session.submitted_at && (
            <div>
              <dt>Submitted</dt>
              <dd>{formatTimestamp(session.submitted_at)}</dd>
            </div>
          )}
          {session.posting_document_no && (
            <div>
              <dt>Posted as</dt>
              <dd>
                <code>{session.posting_document_no}</code>
              </dd>
            </div>
          )}
        </dl>

        <p className="muted small">{affordance.why}</p>
        <Link
          className="button-link"
          to={sessionPath(areaId, session, affordance.route)}
          data-testid={`open-${session.session_id}`}
        >
          {affordance.label}
        </Link>
      </div>
    </li>
  );
}
